import React, { useEffect, useState } from 'react';
import api from '../../api';
import type { PromotionDefinition } from './promotionTypes';

type WhatsAppMode = 'auto' | 'message' | 'card' | 'none';

interface TestRecipient {
  id: string;
  name: string | null;
  email: string | null;
  whatsapp: boolean;
}

interface TestResult {
  userId: string;
  name: string | null;
  whatsapp: {
    sent: boolean;
    format: 'message' | 'card' | 'carousel' | null;
    worded?: boolean;
    fallbackReason?: string | null;
    language?: string;
    shown?: boolean;
    error?: string;
  } | null;
  briefing: { sent: boolean; url?: string; error?: string } | null;
}

interface PromotionTestPanelProps {
  slug: string;
  definition: PromotionDefinition;
  cardApproved: boolean;
  disabledReason?: string | null;
  fixedContent?: boolean;
  auth: () => Promise<{ headers: { Authorization: string } }>;
}

const FALLBACK_REASONS: Record<string, string> = {
  wording_off: 'secretary wording is off for this account',
  error: 'the wording step errored',
  no_draft: 'the secretary returned no wording',
  no_conversation: 'they have no chat history with their secretary yet',
  too_long: 'the secretary’s wording was too long',
  review_exhausted: 'the secretary’s wording failed review',
  not_worded: 'it was not worded',
};

const fallbackReason = (reason: string | null | undefined) => {
  if (!reason) return 'unknown reason';
  if (reason.startsWith('missing_fact')) {
    return `the wording left out ${reason.slice('missing_fact'.length).trim()}`;
  }
  return FALLBACK_REASONS[reason] ?? reason.replace(/_/g, ' ');
};

const whatsappSummary = (
  whatsapp: NonNullable<TestResult['whatsapp']>,
  fixedContent: boolean,
) => {
  if (!whatsapp.sent) return `WhatsApp failed: ${whatsapp.error ?? 'not sent'}`;
  if (fixedContent && whatsapp.format === 'message') {
    return whatsapp.shown === false
      ? 'WhatsApp text and link waiting behind the "I have an update" template'
      : 'WhatsApp text and link sent';
  }
  if (whatsapp.format === 'card') {
    return `WhatsApp card sent (${(whatsapp.language ?? 'en').toUpperCase()})`;
  }
  if (whatsapp.format === 'carousel') {
    return `WhatsApp carousel sent (${(whatsapp.language ?? 'en').toUpperCase()})`;
  }
  const wording = whatsapp.worded
    ? 'worded by the secretary'
    : `fallback text sent, because ${fallbackReason(whatsapp.fallbackReason)}`;
  return whatsapp.shown === false
    ? `WhatsApp message waiting behind the "I have an update" template, ${wording}`
    : `WhatsApp message sent, ${wording}`;
};

const PromotionTestPanel: React.FC<PromotionTestPanelProps> = ({
  slug,
  definition,
  cardApproved,
  disabledReason,
  fixedContent = false,
  auth,
}) => {
  const [recipients, setRecipients] = useState<TestRecipient[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const hasWhatsApp = fixedContent || definition.channels.includes('whatsapp');
  const hasBriefing =
    !fixedContent &&
    definition.channels.includes('briefing') &&
    Boolean(definition.briefing);
  const [whatsapp, setWhatsApp] = useState<WhatsAppMode>(
    hasWhatsApp ? 'auto' : 'none',
  );
  const [briefing, setBriefing] = useState(hasBriefing);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<TestResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setRecipients([]);
    setSelected([]);
    void (async () => {
      try {
        const response = await api.get<{
          recipients: TestRecipient[];
          you: string;
        }>(
          `/promotion-stats/promotions/test-recipients?slug=${encodeURIComponent(slug)}`,
          await auth(),
        );
        if (!active) return;
        setRecipients(response.data.recipients);
        setSelected(
          response.data.recipients.some(
            (recipient) =>
              recipient.id === response.data.you && recipient.whatsapp,
          )
            ? [response.data.you]
            : [],
        );
      } catch {
        if (active) setError('Could not load the staff list.');
      }
    })();
    return () => {
      active = false;
    };
  }, [auth, slug]);

  const toggle = (id: string, on: boolean) => {
    if (
      on &&
      !recipients.some((recipient) => recipient.id === id && recipient.whatsapp)
    ) {
      return;
    }
    setSelected((prev) =>
      (on ? [...prev, id] : prev.filter((existing) => existing !== id)).filter(
        (selectedId) =>
          recipients.some(
            (recipient) => recipient.id === selectedId && recipient.whatsapp,
          ),
      ),
    );
  };

  const whatsappMode: WhatsAppMode = fixedContent
    ? 'auto'
    : hasWhatsApp
      ? whatsapp
      : 'none';
  const sendBriefing = hasBriefing && briefing;
  const nothingToSend = whatsappMode === 'none' && !sendBriefing;
  const nameOf = (id: string) =>
    recipients.find((recipient) => recipient.id === id)?.name ?? 'Unnamed';

  const send = async () => {
    const parts = [
      whatsappMode === 'auto'
        ? 'the WhatsApp message or card they would get right now'
        : whatsappMode === 'message'
          ? 'the WhatsApp message'
          : whatsappMode === 'card'
            ? 'the WhatsApp card'
            : null,
      sendBriefing ? 'a Daily Briefing link' : null,
    ].filter(Boolean);
    const confirmation = fixedContent
      ? `Send the What's new carousel (or the text and link where their language isn't approved yet) to ${selected.map(nameOf).join(', ')} on WhatsApp now?`
      : `Send ${parts.join(' and ')} to ${selected.map(nameOf).join(', ')} on WhatsApp now?`;
    if (!window.confirm(confirmation)) {
      return;
    }
    setSending(true);
    setError(null);
    setResults(null);
    try {
      const response = await api.post<{ results: TestResult[] }>(
        `/promotion-stats/promotions/${encodeURIComponent(slug)}/test`,
        fixedContent
          ? { userIds: selected }
          : {
              userIds: selected,
              whatsapp: whatsappMode,
              briefing: sendBriefing,
            },
        await auth(),
      );
      setResults(response.data.results);
    } catch (caught) {
      const data = (
        caught as {
          response?: { data?: { error?: string; results?: TestResult[] } };
        }
      ).response?.data;
      setError(data?.error ?? 'Could not send the test.');
      if (data?.results) setResults(data.results);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className='launch-card promotion-test-panel'>
      <div className='launch-card-header'>
        <h4>Test this promotion</h4>
      </div>
      <p className='launch-hint'>
        {fixedContent
          ? "Sends now, exactly what they'd get from Start. Doesn't enrol them and isn't counted in stats. Taps on a test aren't saved."
          : "Sends now. Skips the schedule, audience, opt-outs and the 14-day limit, and isn't counted in stats. Taps on a test briefing card aren't saved."}
      </p>
      <fieldset className='promotion-test-panel__people'>
        <legend>Send to</legend>
        {recipients.map((recipient) => (
          <label
            className={`promotion-check${recipient.whatsapp ? '' : ' promotion-check--disabled'}`}
            key={recipient.id}
          >
            <input
              type='checkbox'
              checked={selected.includes(recipient.id)}
              disabled={!recipient.whatsapp}
              onChange={(event) => toggle(recipient.id, event.target.checked)}
            />
            <span className='promotion-test-panel__recipient-details'>
              <span>{recipient.name ?? 'Unnamed'}</span>
              {(recipient.email || !recipient.whatsapp) && (
                <span className='launch-hint'>
                  {recipient.email}
                  {recipient.email && !recipient.whatsapp ? ' · ' : null}
                  {!recipient.whatsapp ? 'no WhatsApp' : null}
                </span>
              )}
            </span>
          </label>
        ))}
      </fieldset>
      <div className='promotion-inline-fields'>
        {!fixedContent && hasWhatsApp && (
          <label>
            WhatsApp
            <select
              value={whatsapp}
              onChange={(event) =>
                setWhatsApp(event.target.value as WhatsAppMode)
              }
            >
              <option value='auto'>What they&apos;d get right now</option>
              {!definition.whatsapp?.cardOnly && (
                <option value='message'>Secretary message</option>
              )}
              {definition.card && (
                <option value='card' disabled={!cardApproved}>
                  {cardApproved ? 'Card' : 'Card (not approved yet)'}
                </option>
              )}
              <option value='none'>Don&apos;t send</option>
            </select>
          </label>
        )}
        {!fixedContent && hasBriefing && (
          <label className='promotion-check'>
            <input
              type='checkbox'
              checked={briefing}
              onChange={(event) => setBriefing(event.target.checked)}
            />
            Daily Briefing link
          </label>
        )}
      </div>
      {!fixedContent &&
        whatsappMode !== 'message' &&
        whatsappMode !== 'none' &&
        definition.card && (
          <p className='launch-hint'>
            Cards are WhatsApp templates and cost the usual template fee.
          </p>
        )}
      <div className='promotion-review-actions'>
        <button
          type='button'
          className='launch-btn launch-btn--quiet'
          disabled={
            sending ||
            Boolean(disabledReason) ||
            selected.length === 0 ||
            nothingToSend
          }
          title={disabledReason ?? undefined}
          onClick={() => void send()}
        >
          {sending ? 'Sending…' : 'Send test'}
        </button>
      </div>
      {disabledReason && <p className='launch-hint'>{disabledReason}</p>}
      {error && (
        <div className='launch-error' role='alert'>
          {error}
        </div>
      )}
      {results && (
        <ul className='promotion-test-panel__results' role='status'>
          {results.map((result) => (
            <li key={result.userId}>
              <strong>{result.name ?? 'Unnamed'}</strong>
              {result.whatsapp && (
                <span>{whatsappSummary(result.whatsapp, fixedContent)}</span>
              )}
              {result.briefing && (
                <span>
                  {result.briefing.sent
                    ? 'Daily Briefing link sent'
                    : `Daily Briefing failed: ${result.briefing.error ?? 'not sent'}`}
                  {result.briefing.url && (
                    <>
                      {' '}
                      (
                      <a
                        href={result.briefing.url}
                        target='_blank'
                        rel='noreferrer'
                      >
                        open
                      </a>
                      )
                    </>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PromotionTestPanel;
